import { WebLayout } from "components/Layout";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Clock } from "react-feather";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.45, ease: "easeOut" },
  }),
};

export function ContactUs() {
  return (
    <WebLayout>
      {/* Header */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">
              Get in Touch
            </span>
            <h1 className="mt-4 text-4xl font-extrabold text-white md:text-5xl">
              Let’s talk about your project
            </h1>
            <p className="mt-4 text-gray-300 md:text-lg">
              Share measurements, photos, or ideas—our team will respond quickly
              with options and a free quote.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Contact Panel */}
      <section className="bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-18 grid gap-10 md:grid-cols-3">
          {/* Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="md:col-span-2 rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-6"
          >
            <h2 className="text-xl font-semibold text-white">
              Send us a message
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              We typically reply the same business day.
            </p>

            <form
              action="https://formspree.io/f/your-id" // ← replace with your Formspree endpoint or your backend
              method="POST"
              className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <label className="text-sm text-gray-300">
                Name
                <input
                  required
                  name="name"
                  type="text"
                  placeholder="Your full name"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white placeholder-gray-500 outline-none focus:border-green-500"
                />
              </label>

              <label className="text-sm text-gray-300">
                Email
                <input
                  required
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white placeholder-gray-500 outline-none focus:border-green-500"
                />
              </label>

              <label className="text-sm text-gray-300">
                Phone
                <input
                  name="phone"
                  type="tel"
                  placeholder="+256 ..."
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white placeholder-gray-500 outline-none focus:border-green-500"
                />
              </label>

              <label className="text-sm text-gray-300">
                Service Type
                <select
                  name="service"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white outline-none focus:border-green-500"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select a service
                  </option>
                  <option>Windows</option>
                  <option>Doors</option>
                  <option>Burglarproofing</option>
                  <option>Stairs & Railings</option>
                  <option>Furniture</option>
                  <option>Pagolas & Balconies</option>
                  <option>Door & Window Fittings</option>
                  <option>Tangstands</option>
                  <option>Other</option>
                </select>
              </label>

              <label className="sm:col-span-2 text-sm text-gray-300">
                Message
                <textarea
                  required
                  name="message"
                  rows={6}
                  placeholder="Tell us about your space, measurements, and any design ideas..."
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white placeholder-gray-500 outline-none focus:border-green-500"
                />
              </label>

              {/* Optional file attach (kept simple; ensure your backend supports it) */}
              {/* <label className="sm:col-span-2 text-sm text-gray-300">
                Attach Photos (optional)
                <input
                  type="file"
                  name="attachments"
                  multiple
                  className="mt-1 block w-full text-sm text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-white hover:file:bg-white/20"
                />
              </label> */}

              {/* Honeypot */}
              <input
                type="text"
                name="_gotcha"
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="sm:col-span-2 mt-2 flex flex-wrap gap-3">
                <button
                  type="submit"
                  className="inline-flex items-center rounded-lg bg-green-500 px-5 py-3 font-semibold text-black shadow hover:brightness-105"
                >
                  Send Message
                </button>
                <a
                  href="mailto:info@crutialartworks.com"
                  className="inline-flex items-center rounded-lg border border-white/15 px-5 py-3 font-semibold text-white hover:bg-white/10"
                >
                  Email Instead
                </a>
                <a
                  href="https://wa.me/256779706977"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-lg border border-white/15 px-5 py-3 font-semibold text-white hover:bg-white/10"
                >
                  WhatsApp
                </a>
              </div>
            </form>
          </motion.div>

          {/* Contact Panel */}
          <motion.aside
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-6"
          >
            <h3 className="text-lg font-semibold text-white">Quick Contacts</h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-green-400" />
                <a href="tel:+256779706977" className="hover:text-green-400">
                  +256 779 706 977 (WhatsApp)
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-green-400" />
                <a href="tel:+256701175751" className="hover:text-green-400">
                  +256 701 175 751
                </a>
              </li>
              {/* <li className="flex items-center gap-2">
                <Mail size={16} className="text-green-400" />
                <a
                  href="mailto:info@crutialartworks.com"
                  className="hover:text-green-400"
                >
                  info@crutialartworks.com
                </a>
              </li> */}
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-green-400" />
                <span>Kito opposite tendo mart</span>
              </li>
              <li className="flex items-center gap-2">
                <Clock size={16} className="text-green-400" />
                <span>Mon–Sat: 8:30am – 6:00pm (EAT)</span>
              </li>
            </ul>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <a
                href="https://wa.me/256779706977"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg bg-green-500 px-4 py-2 font-semibold text-black hover:brightness-105"
              >
                <MessageCircle size={18} className="mr-2" /> WhatsApp
              </a>
              <a
                href="tel:+256779706977"
                className="inline-flex items-center justify-center rounded-lg border border-white/15 px-4 py-2 font-semibold text-white hover:bg-white/10"
              >
                <Phone size={18} className="mr-2" /> Call Now
              </a>
            </div>
          </motion.aside>
        </div>
      </section>

      {/* Map */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.45 }}
            className="overflow-hidden rounded-2xl border border-white/10"
          >
            <iframe
              title="Map to Crutial Touch Art & Crafts"
              src="https://www.google.com/maps?q=Kasangati+Road,+Opposite+Court,+Kampala,+Uganda&output=embed"
              className="h-80 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>
    </WebLayout>
  );
}
